import { RunContext } from '@openai/agents';
import type { FunctionTool } from '@openai/agents/realtime';
import { LiveVoiceSession } from '@/lib/live-voice-session';
import {
  LIVE_VOICE_FUNCTION_LIMITS,
  LiveVoiceToolNotExecutedError,
} from '@/lib/live-voice-function-calls';
import { hasValidVoiceCallToolInput } from '@/lib/voice-call-tool-input';
import { createLiveVoiceEventHandlers } from './register-realtime-voice-events';
import type { StartRealtimeVoiceCallArgs } from './start-realtime-voice-call';
import type { RealtimeVoiceFailureKind } from './realtime-voice-lifecycle';
import { getActiveRealtimeVoiceQuestionnaire } from '@/lib/realtime-voice-questionnaire';
import type { Id } from '@/convex/_generated/dataModel';

type Args = {
  args: StartRealtimeVoiceCallArgs;
  tools: Pick<FunctionTool, 'name' | 'invoke'>[];
  credential: { leaseSessionId: string };
  micStream: MediaStream;
  assistantAudioElement: HTMLAudioElement;
  orgThreadId: Id<'orgThreads'>;
  isCurrentCall: () => boolean;
  onRemoteStream: (stream: MediaStream) => void;
  terminateFailedCall: (failure: RealtimeVoiceFailureKind) => void;
};

export function createRealtimeVoiceRuntime(options: Args) {
  const { args } = options;
  const handlers = createLiveVoiceEventHandlers(
    args,
    options.credential.leaseSessionId,
    options.terminateFailedCall
  );
  const tools = new Map(options.tools.map((tool) => [tool.name, tool]));
  const questionnaireRevisions = new Map<string, string | null>();
  const session = new LiveVoiceSession({
    micStream: options.micStream,
    audioElement: options.assistantAudioElement,
    isCurrentCall: options.isCurrentCall,
    onRemoteStream: options.onRemoteStream,
    ...handlers,
    onEvent(event) {
      if (event.type === 'session.delegation.created') {
        const delegation = event.delegation;
        if (
          delegation &&
          typeof delegation === 'object' &&
          'id' in delegation &&
          typeof delegation.id === 'string' &&
          !questionnaireRevisions.has(delegation.id)
        ) {
          if (
            questionnaireRevisions.size >=
            LIVE_VOICE_FUNCTION_LIMITS.delegations
          ) {
            options.terminateFailedCall('capacity');
            return;
          }
          questionnaireRevisions.set(
            delegation.id,
            getActiveRealtimeVoiceQuestionnaire(String(args.orgThreadId))
              ?.promptKey ?? null
          );
        }
      }
      handlers.onEvent(event);
    },
    async executeTool(call) {
      const tool = tools.get(call.name);
      if (!options.isCurrentCall() || !tool) {
        return 'This operation is unavailable for this call. Do not retry automatically.';
      }
      if (!hasValidVoiceCallToolInput(call.name, call.arguments)) {
        throw new LiveVoiceToolNotExecutedError('invalid_arguments');
      }
      if (
        call.name === 'answer_current_question' &&
        questionnaireRevisions.get(call.delegationId) !==
          (getActiveRealtimeVoiceQuestionnaire(String(args.orgThreadId))
            ?.promptKey ?? null)
      ) {
        return 'The visible question changed while this answer was pending. Ask the current question; no answer was saved.';
      }
      args.responses.activeOperationRef.current = call.delegationId;
      try {
        const result: unknown = await tool.invoke(
          new RunContext(),
          call.arguments
        );
        return typeof result === 'string'
          ? result
          : (JSON.stringify(result) ?? 'No result was returned.');
      } finally {
        args.responses.activeOperationRef.current = null;
      }
    },
  });
  return { session };
}
